import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, ClipboardCheck, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

export default function Inspections() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ asset_id: "", inspection_type: "Routine", result: "pass", condition_rating: "Good", findings: "", recommendations: "" });
  const [files, setFiles] = useState([]);

  const load = useCallback(async () => {
    const {data} = await api.get("/inspections");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/assets").then((r)=>setAssets(r.data)); }, []);

  const save = async () => {
    try {
      const { data } = await api.post("/inspections", f);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach((file) => fd.append("files", file));
        try { await api.post(`/inspections/${data.id}/photos`, fd); }
        catch { toast.error("Photos failed to upload"); }
      }
      toast.success("Inspection recorded"); setOpen(false);
      setFiles([]); setF({ asset_id: "", inspection_type: "Routine", result: "pass", condition_rating: "Good", findings: "", recommendations: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="inspections-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Field records</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Inspections</h1>
          <p className="text-sm text-slate-600 mt-1">{rows.length} inspection{rows.length===1?"":"s"} recorded</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-inspection-btn"><Plus className="w-4 h-4 mr-1"/>New inspection</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Record inspection</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Asset *</Label>
                <Select value={f.asset_id} onValueChange={(v)=>setF({...f, asset_id:v})}>
                  <SelectTrigger data-testid="insp-asset"><SelectValue placeholder="Select asset"/></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {assets.map((a)=>(<SelectItem key={a.id} value={a.id}>{a.name} — {a.location||a.asset_group}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Type</Label><Input value={f.inspection_type} onChange={(e)=>setF({...f, inspection_type:e.target.value})}/></div>
              <div><Label>Result</Label>
                <Select value={f.result} onValueChange={(v)=>setF({...f, result:v})}>
                  <SelectTrigger data-testid="insp-result"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pass">Pass</SelectItem>
                    <SelectItem value="attention_required">Attention required</SelectItem>
                    <SelectItem value="fail">Fail (auto-creates defect)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Condition</Label>
                <Select value={f.condition_rating} onValueChange={(v)=>setF({...f, condition_rating:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{["Excellent","Good","Fair","Poor","Unserviceable"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Findings</Label><Textarea value={f.findings} onChange={(e)=>setF({...f, findings:e.target.value})}/></div>
              <div><Label>Recommendations</Label><Textarea value={f.recommendations} onChange={(e)=>setF({...f, recommendations:e.target.value})}/></div>
              <div>
                <Label>Photos (optional)</Label>
                <input type="file" multiple accept="image/*" capture="environment"
                  onChange={(e)=>setFiles(Array.from(e.target.files||[]))}
                  className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-slate-200 file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
                  data-testid="insp-photos"/>
                {files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {files.map((file, i)=>(
                      <div key={`${file.name}-${file.size}-${i}`} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3"/><span className="truncate max-w-[10rem]">{file.name}</span>
                        <button type="button" className="text-slate-400 hover:text-slate-700" onClick={()=>setFiles(files.filter((_,j)=>j!==i))}><X className="w-3 h-3"/></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={save} disabled={!f.asset_id} data-testid="insp-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200"><CardContent className="p-4">
        <ul className="divide-y divide-slate-100">
          {rows.map((i)=>{
            const a = assets.find((x)=>x.id===i.asset_id);
            const tone = i.result==="fail"?"bg-red-100 text-red-800":i.result==="attention_required"?"bg-amber-100 text-amber-800":"bg-emerald-100 text-emerald-800";
            return (
              <li key={i.id} className="py-3 flex items-start gap-3">
                <span className={`p-2 rounded-md ${tone} mt-0.5`}><ClipboardCheck className="w-4 h-4"/></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium">{a?.name || i.asset_id}</div>
                    <Badge className={`${tone} border-0`}>{i.result}</Badge>
                    <div className="text-xs text-slate-500 ml-auto">{new Date(i.inspection_date).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-slate-500">{i.inspection_type} · {i.inspector_name} · condition: {i.condition_rating||"—"}</div>
                  {i.findings && <div className="text-sm text-slate-700 mt-1">{i.findings}</div>}
                  {i.photo_ids?.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {i.photo_ids.map((pid)=>(
                        <a key={pid} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${pid}/download`} target="_blank" rel="noreferrer">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${pid}/download`} alt="Inspection photo" className="w-14 h-14 object-cover rounded border border-slate-200 bg-slate-50"/>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {rows.length===0 && <li className="text-center text-slate-500 py-8">No inspections yet — record one from an asset detail page or use “+ New inspection”.</li>}
        </ul>
      </CardContent></Card>
    </div>
  );
}
